import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

export interface InvoiceListOpts {
  org_id?: string;
  status?: admin.InvoiceStatus;
  limit?: number;
}

/**
 * The endpoint defaults to 100 rows and clamps at 500. Asking for the ceiling
 * keeps the console's client-side filtering honest for longer: the page has no
 * server-side paging to fall back on, so a low default silently hid invoices.
 */
const MAX_LIMIT = 500;

export function useInvoices(opts: InvoiceListOpts = {}) {
  const limit = opts.limit ?? MAX_LIMIT;
  return useQuery({
    queryKey: ['admin', 'invoices', opts.org_id ?? null, opts.status ?? null, limit],
    queryFn: () => admin.listInvoices({ org_id: opts.org_id, status: opts.status, limit })
  });
}

export function useInvoice(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: ['admin', 'invoice', id],
    queryFn: () => admin.getInvoice(id!)
  });
}

export function useRegenerateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => admin.regenerateInvoicePdf(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] })
  });
}

/**
 * W41: re-run SARS/FIRS validation for one invoice and persist the verdict.
 * The list carries `validation_status` on every row, so the refreshed verdict
 * has to land back in the cached list rather than only in the response.
 */
export function useValidateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => admin.validateInvoice(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invoices'] })
  });
}

export { MAX_LIMIT as INVOICE_LIST_LIMIT };
